import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { Tables, InsertTables, UpdateTables } from "@/types/database";

async function fetchOrganizers() {
  const { data, error } = await supabase
    .from("organizers")
    .select("*")
    .order("name");

  if (error) throw error;
  return data;
}

async function fetchOrganizerById(id: string) {
  const { data, error } = await supabase
    .from("organizers")
    .select("*, clients(*)")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
}

async function createOrganizer(org: InsertTables<"organizers">) {
  const { data, error } = await supabase
    .from("organizers")
    .insert([org])
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateOrganizer(
  id: string,
  updates: UpdateTables<"organizers">,
) {
  const { data, error } = await supabase
    .from("organizers")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteOrganizer(id: string) {
  const { error } = await supabase.from("organizers").delete().eq("id", id);

  if (error) throw error;
}

export function useOrganizers() {
  return useQuery({
    queryKey: ["organizers"],
    queryFn: fetchOrganizers,
  });
}

export function useOrganizer(id: string) {
  return useQuery({
    queryKey: ["organizer", id],
    queryFn: () => fetchOrganizerById(id),
    enabled: !!id,
  });
}

export function useCreateOrganizer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOrganizer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizers"] });
    },
  });
}

export function useUpdateOrganizer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateTables<"organizers">;
    }) => updateOrganizer(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["organizers"] });
      queryClient.invalidateQueries({ queryKey: ["organizer", id] });
    },
  });
}

export function useDeleteOrganizer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteOrganizer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizers"] });
    },
  });
}

export type Organizer = Tables<"organizers">;
